import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { emailChannelsApi, type EmailConfiguration, type EmailDraft } from '@/api/emailChannels';
import { Loading } from '@/components/ui';

const inputClass = 'w-full rounded-lg border border-navy-border bg-navy-dark px-3 py-2 text-sm text-white disabled:opacity-60';
function Editor({ orgId, config }: { orgId: string; config: EmailConfiguration }) {
  const queryClient = useQueryClient();
  const [source, setSource] = useState<EmailDraft['key_source']>(config.channel?.has_key ? 'keep' : config.platform_available ? 'platform' : 'custom');
  const [name, setName] = useState(config.channel?.from_name ?? '');
  const [sender, setSender] = useState(config.channel?.from_email ?? config.platform_sender);
  const [key, setKey] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const channel = config.channel;
  const draft = (): EmailDraft => ({ organization_id: orgId, id: channel?.id ?? null, updated_at: channel?.updated_at ?? null,
    from_name: name, from_email: sender, key_source: source,
    ...(source === 'custom' ? { api_key: key } : {}) });
  const operation = useMutation({
    mutationFn: async (action: 'save' | 'verify' | 'suspend') => {
      setNotice(''); setError('');
      if (action === 'verify') { await emailChannelsApi.verify(draft()); return action; }
      if (action === 'suspend' && channel) await emailChannelsApi.suspend(orgId, channel);
      else await emailChannelsApi.save(draft());
      return action;
    },
    onSuccess: async action => {
      setNotice(action === 'verify' ? 'הדומיין מאומת לשליחה. עדיין לא נשמר שינוי.' : action === 'save' ? 'ערוץ האימייל נשמר והופעל.' : 'ערוץ האימייל הושהה.');
      if (action !== 'verify') { setKey(''); await queryClient.invalidateQueries({ queryKey: ['org_email_channel', orgId] }); }
    },
    onError: (e: Error) => setError(e.message),
    // React Query must not retain a draft/API key in mutation variables or data.
    gcTime: 0,
  });
  const changed = () => { setNotice(''); setError(''); };
  const unsupported = !!channel && channel.provider !== 'resend';
  return <form onSubmit={e => { e.preventDefault(); operation.mutate('save'); }} className="space-y-4">
    <p className="text-sm text-slate-300">{channel ? `מצב הערוץ: ${channel.status === 'active' && channel.is_primary ? 'פעיל וראשי' : 'לא פעיל לשליחת קוד'}` : 'לא הוגדר ערוץ אימייל לארגון.'}</p>
    <p className="text-xs text-slate-400">כתובת הקשר של הארגון אינה בהכרח כתובת שולח. אפשר להשתמש בחיבור המערכתי או בדומיין ארגוני מאומת ב־Resend. השמירה אינה שולחת הודעה.</p>
    {unsupported ? <p role="alert" className="text-amber-300">הערוץ הקיים משתמש בספק אחר. נדרש בירור לפני החלפה.</p> : <>
      <fieldset disabled={operation.isPending} className="space-y-4">
        <label className="block text-sm text-slate-300">חיבור השליחה
          <select className={inputClass} value={source} onChange={e => { setSource(e.target.value as EmailDraft['key_source']); setKey(''); changed(); }}>
            {channel?.has_key && <option value="keep">השארת המפתח הארגוני השמור</option>}
            {config.platform_available && <option value="platform">Resend מערכתי — ללא הזנת מפתח</option>}
            <option value="custom">מפתח Resend ארגוני</option>
          </select>
        </label>
        <label className="block text-sm text-slate-300">שם השולח
          <input required maxLength={100} className={inputClass} value={name} onChange={e => { setName(e.target.value); changed(); }} />
        </label>
        <label className="block text-sm text-slate-300">כתובת השולח
          <input required type="email" maxLength={254} dir="ltr" className={inputClass} value={sender} onChange={e => { setSender(e.target.value); changed(); }} />
        </label>
        {source === 'custom' && <label className="block text-sm text-slate-300">מפתח Resend
          <input required type="password" autoComplete="off" maxLength={512} dir="ltr" className={inputClass} value={key} onChange={e => { setKey(e.target.value); changed(); }} />
          <span className="block mt-1 text-xs text-slate-500">המפתח נשמר בשרת ולא מוצג שוב. אימות הדומיין דורש הרשאת קריאת דומיינים בחשבון Resend.</span>
        </label>}
        <div className="flex flex-wrap gap-3">
          <button type="submit" className="rounded-lg bg-gold px-4 py-2 text-sm font-bold text-navy-dark">{operation.isPending ? 'בודק ושומר...' : 'אימות דומיין ושמירת ערוץ'}</button>
          <button type="button" onClick={() => operation.mutate('verify')} className="rounded-lg border border-navy-border px-4 py-2 text-sm text-white">בדיקת דומיין בלבד</button>
          {channel?.status === 'active' && <button type="button" onClick={() => operation.mutate('suspend')} className="rounded-lg border border-amber-400/40 px-4 py-2 text-sm text-amber-300">השהיית שליחת אימייל</button>}
        </div>
      </fieldset>
    </>}
    {notice && <p role="status" className="text-sm text-green-400">{notice}</p>}
    {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
  </form>;
}
export default function OrgEmailChannelSection({ orgId }: { orgId: string }) {
  const query = useQuery({ queryKey: ['org_email_channel', orgId], queryFn: () => emailChannelsApi.get(orgId), retry: false });
  return <section dir="rtl" className="rounded-xl border border-navy-border bg-navy-card p-5">
    <h3 className="mb-4 flex items-center gap-2 font-semibold text-white"><Mail className="h-4 w-4 text-gold" />ערוץ אימייל — Resend</h3>
    {query.isPending ? <Loading text="טוען הגדרת אימייל..." /> : query.isError ? <div role="alert" className="text-sm text-red-400">{query.error.message}<button type="button" onClick={() => void query.refetch()} className="mr-3 underline">רענון</button></div> :
      <Editor key={`${orgId}:${query.data.channel?.updated_at ?? 'new'}`} orgId={orgId} config={query.data} />}
  </section>;
}
